import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import type { UserAction } from "../aiSdkMessages";
import { CHAT_ENTRIES_PREVIOUS_ENTRY_ID_UNIQUE } from "../dbConstraints";
import { isUniqueConstraintViolation } from "../dbErrors";
import { zql } from "../generatedDrizzleSchema";
import { type AttributionCtx, actorColumns } from "../rbac/roles";
import { stripNullBytesDeep } from "../stripNullBytes";
import { userFacingError } from "../userFacingError";
import { clientGeneratableIdSchema, generateId } from "../id";

export { makeBranchParams } from "../copyOnWrite";

export function nextRespondingAt(current: number | null): number {
  return Math.max(Date.now(), (current ?? 0) + 1);
}

export const branchSchema = z.object({
  branchId: z.string(),
  forkBranchId: clientGeneratableIdSchema,
  forkCommitId: clientGeneratableIdSchema,
  forkChatId: clientGeneratableIdSchema,
  workingCommitId: clientGeneratableIdSchema,
  workingChatId: clientGeneratableIdSchema,
});

// Values can carry uploaded file bytes (e.g. a connector CA certificate), and
// Postgres rejects null bytes in jsonb.
export const stringRecord = z
  .record(z.string(), z.string())
  .transform(stripNullBytesDeep);

export async function insertUserInterruptEntry(
  tx: Transaction,
  entry: {
    id: string;
    chatId: string;
    createdAt: number;
    previousEntryId: string | null;
    tenantId: string;
  }
) {
  try {
    await tx.mutate.chatEntries.insert({
      ...entry,
      entryType: "UserInterrupt",
    });
  } catch (err) {
    if (
      isUniqueConstraintViolation(err, CHAT_ENTRIES_PREVIOUS_ENTRY_ID_UNIQUE)
    ) {
      throw userFacingError("Couldn’t stop the agent, try again");
    }
    throw err;
  }
}

export async function insertUserAction(
  tx: Transaction,
  commitId: string,
  ctx: AttributionCtx,
  tenantId: string,
  actionType: UserAction["actionType"],
  metadata: UserAction["metadata"]
) {
  // user_actions is a human audit trail with no service-account column, so a
  // pure service account records no marker.
  const { userId } = actorColumns(ctx);
  if (!userId) return;
  const [chat] = await tx.run(zql.chats.where("commitId", commitId));
  if (!chat) return;

  const lastEntries = await tx.run(
    zql.chatEntries
      .where("chatId", chat.id)
      .orderBy("createdAt", "desc")
      .limit(1)
  );

  const entryId = generateId();
  const now = Date.now();

  await tx.mutate.chatEntries.insert({
    id: entryId,
    chatId: chat.id,
    entryType: "UserAction",
    createdAt: now,
    previousEntryId: lastEntries[0]?.id ?? null,
    tenantId,
  });

  await tx.mutate.userActions.insert({
    entryId,
    userId,
    actionType,
    metadata,
    tenantId,
  });
}
